/**
 * Skill and rank grades.
 *
 * Grade descends into mastery: 9 is the entry tier a character starts at, 0 the
 * grandmaster tier. Naming the tier stops "0 / 10" from reading as a score of
 * zero, and ordering weakest-first makes a school's skill list read as the
 * progression a player actually walks.
 */

export const GRADE_TIERS = {
    9: 'Novice',
    8: 'Initiate',
    7: 'Apprentice',
    6: 'Adept',
    5: 'Mercenary',
    4: 'Champion',
    3: 'Hero',
    2: 'Paragon',
    1: 'Ascendant',
    0: 'Omniscient',
};

/** Grades in progression order: the first skill a character can take, first. */
export const GRADES_WEAKEST_FIRST = [9, 8, 7, 6, 5, 4, 3, 2, 1, 0];

export function gradeTier(grade) {
    return GRADE_TIERS[grade] ?? null;
}

export function gradeLabel(grade) {
    if (grade == null) return '—';
    const tier = gradeTier(grade);
    return tier ? `${grade} · ${tier}` : String(grade);
}

/**
 * Sorts skills into the order they are learned — weakest grade first, then
 * alphabetically inside a grade. Returns only the grades that hold skills, so
 * a school never shows ten empty headings.
 */
export function groupByGrade(skills) {
    const buckets = new Map();
    for (const skill of skills) {
        const grade = skill.grade ?? 9;
        const bucket = buckets.get(grade);
        if (bucket) bucket.push(skill);
        else buckets.set(grade, [skill]);
    }

    return GRADES_WEAKEST_FIRST.filter((grade) => buckets.has(grade)).map((grade) => ({
        grade,
        tier: gradeTier(grade),
        label: gradeLabel(grade),
        skills: buckets.get(grade).sort((a, b) => a.name.localeCompare(b.name)),
    }));
}
